'use client';

import {
  sendExperienceEnquiry,
  sendShortlistEnquiry,
} from '@/actions/experiences';
import { useTranslation } from '@/contexts/dictionaryContext';
import useExperienceContext from '@/hooks/useExperienceContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useLocale } from '@/hooks/useLocale';
import useUserContext from '@/hooks/useUserContext';
import { cn } from '@/lib/utils';
import {
  ExperienceMedia,
  SendExperienceEnquiryRequest,
} from '@/services/experienceTypes';
import { VillageMedia } from '@/services/types';
import { IExperience } from '@/types/Experience';
import { IVilla } from '@/types/Villa';
import getMediaFilterUrl from '@/utils/get-media-filter';
import { yupResolver } from '@hookform/resolvers/yup';
import { setCookie } from 'cookies-next';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../Button';
import { getSelectDescription } from '../ContactForm/helpers';
import CountrySelect from '../CountrySelect';
import DatePicker from '../DatePicker';
import GlobalLoader from '../GlobalLoader';
import Input from '../Input';
import LoginModal from '../LoginModal';
import QuantitieInputBox from '../QuantitieInput/Box';
import QuantitieInputRoot from '../QuantitieInput/Root';
import TextArea from '../Textarea';
import SubmittedModal from './SubmittedModal';
import { getTranslatedSchema } from './schema';

type EnquiryFormProps = {
  media: ExperienceMedia[] | VillageMedia[];
  description: string;
  bannerContent?: JSX.Element;
  onCancel?: () => void;
  isForExperience?: boolean;
  isForShortlist?: boolean;
  isModal?: boolean;
  villageId?: number | null;
  experienceId?: number | null;
  experienceTitle?: string;
};

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function EnquiryForm({
  media,
  description,
  bannerContent,
  onCancel,
  isForExperience = false,
  isForShortlist = false,
  isModal = false,
  villageId,
  experienceId,
  experienceTitle,
}: EnquiryFormProps) {
  const lang = useLocale();
  const {
    enquiryForm: formDict,
    countries: countriesDict,
    datePicker: datePickerDict,
    modals: modalsDict,
  } = useTranslation();

  const { customer, user, isLogged } = useUserContext();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showDatesError, setShowDatesError] = useState(false);
  const [openSubmittedModal, setOpenSubmittedModal] = useState(false);
  const [villaShortList] = useLocalStorage('villas', [] as IVilla[]);
  const [experienceShortList] = useLocalStorage(
    'experience',
    [] as IExperience[],
  );
  const [blockSubmit, setBlockSubmit] = useState(false);

  const {
    enquiryAdultsTotal,
    enquiryChildrenTotal,
    enquiryInfantsTotal,
    enquiryDate,
  } = useExperienceContext();

  const shortlist = {
    villas: villaShortList.map((item) => item.id),
    experience: experienceShortList.map((item) => item.id),
  };

  const shortlistString = JSON.stringify(shortlist);

  setCookie('shortlist', shortlistString, { maxAge: 60 * 60 * 24 * 7 });

  const coverMedia = media.filter((medias) => medias.type == 'cover')[0];
  const imgCover =
    coverMedia &&
    (getMediaFilterUrl({
      mediaFilters: coverMedia.mediaFilters,
      filterName: '480w_webp',
    }) ??
      getMediaFilterUrl({
        mediaFilters: coverMedia.mediaFilters,
        filterName: '1920w_webp',
      }) ??
      coverMedia.mediaFilters?.[0].url);

  const schema = getTranslatedSchema(formDict.errors);

  const { countries } = getSelectDescription(lang, countriesDict);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const confirmedDates = watch('dates');

  function handleSetCountry(code: string, name: string, id: number) {
    setValue('country_info.name', name);
    setValue('country_info.code', code);
    setValue('country_info.id', id);
    const foundCountry = countries.find((country) => country.name === name);

    if (foundCountry) {
      setValue('country_info.phone.code', foundCountry.id);
      setValue('country_info.phone.country', foundCountry.name);
    }
  }

  const onSubmitClick = () => {
    setShowDatesError(false);
    if (
      !confirmedDates ||
      confirmedDates?.start === null ||
      confirmedDates?.end === null
    ) {
      setShowDatesError(true);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (blockSubmit) return;
    setBlockSubmit(true);

    const timestampStart = moment(data.dates.start).format('YYYY-MM-DD');
    const timestampEnd = moment(data.dates.end).format('YYYY-MM-DD');

    const flexibleDates = data.flexible ?? false;
    const note = data.note ?? '';

    const enquiry: SendExperienceEnquiryRequest = {
      villa: villageId ? villageId.toString() : null,
      experience: experienceId ? experienceId.toString() : null,
      timestampStart,
      timestampEnd,
      customer: null,
      title: null,
      firstName: data.name,
      lastName: data.surname,
      country: data.country_info.id,
      email: data.email,
      phoneCode: data.country_info.id,
      phone: data.phone.toString(),
      adults: data.adults,
      children: data.children,
      infants: data.infants,
      flexibleDates,
      note,
      availabilityNotification: false,
      privacyPolicy: data.terms,
      language: lang,
    };

    window.dataLayer = window.dataLayer || [];

    try {
      if (isForExperience) {
        const response = await sendExperienceEnquiry(enquiry);
        if (response.data) {
          setOpenSubmittedModal(true);
          reset();
          setBlockSubmit(false);
          window.dataLayer.push({
            event: 'eventInfoExperience',
            eventCategory: 'send_info_experience',
            eventAction: 'form_experience',
            eventLabel: `${experienceTitle} | ${experienceId}`,
            language: lang,
          });
        } else {
          setBlockSubmit(false);
        }
      }

      if (isForShortlist) {
        const response = await sendShortlistEnquiry(enquiry);
        if (response.message === 'Send Shortlist') {
          setOpenSubmittedModal(true);
          reset();
          setBlockSubmit(false);
          window.dataLayer.push({
            event: 'eventSendWishlist',
            eventCategory: 'wishlist_send',
            eventAction: 'wishlist_send',
            eventLabel: window.location.href,
            language: lang,
          });
        } else {
          setBlockSubmit(false);
        }
      }
    } catch (error) {
      console.log(error);
    }
  });

  useEffect(() => {
    if (openSubmittedModal) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('no-scroll');
      root.classList.add('no-scroll');
    } else {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.remove('no-scroll');
      root.classList.remove('no-scroll');
    }
  }, [openSubmittedModal]);

  useEffect(() => {
    if (isForShortlist) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('no-scroll');
      root.classList.add('no-scroll');
    }
  }, [isForShortlist]);

  useEffect(() => {
    const { name, code, id } = getSelectDescription(lang, countriesDict);

    if (isLogged) {
      const country = countries.find(
        (country) => country.id == customer?.country?.id,
      );
      const countryPhone = countries.find(
        (country) => country.id == customer?.phoneCode?.id,
      );

      const enquiryDefault = {
        name: user.firstName,
        surname: user.lastName,
        email: user.emailCanonical,
        phone: customer.phone ? customer.phone : '',
        country_info: {
          name: country ? country?.name : name,
          code: country ? country?.code : code,
          id: country ? country?.id : id,
          phone: {
            code: countryPhone ? countryPhone?.id : id,
            country: countryPhone ? countryPhone?.name : name,
          },
        },
        adults: 0,
        children: 0,
        infants: 0,
      };
      reset(enquiryDefault);
    } else {
      const enquiryDefault = {
        country_info: {
          name,
          code,
          id,
          phone: {
            code: id,
            country: name,
          },
        },
        adults: 0,
        children: 0,
        infants: 0,
      };
      reset(enquiryDefault);
    }
    // eslint-disable-next-line
  }, [isLogged]);

  useEffect(() => {
    if (enquiryDate.start && enquiryDate.end) {
      setValue('dates', enquiryDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryDate]);

  useEffect(() => {
    setValue('adults', enquiryAdultsTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryAdultsTotal]);

  useEffect(() => {
    setValue('children', enquiryChildrenTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryChildrenTotal]);

  useEffect(() => {
    setValue('infants', enquiryInfantsTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryInfantsTotal]);

  return (
    <>
      <GlobalLoader isLoading={isSubmitting} />
      {openSubmittedModal && (
        <SubmittedModal
          formDict={formDict}
          setOpenSubmittedModal={setOpenSubmittedModal}
        />
      )}
      <div
        id="enquiry"
        className={`flex flex-col desktop:flex-row w-full h-auto mb-8 ${
          isModal && 'mx-auto h-auto !mb-0'
        }`}>
        {!isModal && (
          <div className="flex desktop:w-1/3">
            <div className="desktop:shadow-default desktop:mb-auto desktop:mt-auto bg-white z-[1]">
              <div className="px-10 py-[30px]">
                <h3 className="text-4xl font-averta-semibold leading-[1.25] pb-2">
                  {formDict.title}
                </h3>
                <p className="pb-4">{description}</p>
              </div>
              <div className="relative h-[190px] desktop:h-auto desktop:min-h-[180px]">
                <Image
                  src={imgCover ?? ''}
                  alt="Enquiry"
                  fill
                  className="w-full h-full object-cover"
                  sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                />
                <div className="absolute flex flex-col items-center justify-center w-full h-full px-10 py-5 top-0 left-0 bg-black/[.4]">
                  {bannerContent}
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`flex w-full flex-col desktop:w-2/3 ${
            isModal &&
            'sm:w-[90%] desktop:w-[90%] bg-white max-w-[1050px] mx-auto'
          }`}>
          {isModal && (
            <div className="w-full tablet:px-[60px] p-[30px] text-4xl font-semibold bg-gray-100">
              <h2>{formDict.modalTitle}</h2>
            </div>
          )}

          <form
            className={`flex flex-col gap-4 desktop:shadow-default w-full desktop:w-[110%] desktop:py-[60px] desktop:pr-[60px] desktop:pl-[15%] desktop:-ml-[10%] mt-5 desktop:mt-0 ${
              isModal &&
              'p-[30px] pb-[30px] tablet:pb-[60px] tablet:!px-[60px] desktop:!px-[60px] desktop:pt-[30px] desktop:w-full desktop:ml-0 !mt-0'
            }`}
            onSubmit={onSubmit}>
            {!isLogged && (
              <div className="flex font-averta-semibold gap-2 py-4">
                <p className="text-lg">{formDict.haveAccount}</p>
                <LoginModal dict={modalsDict} text />
              </div>
            )}
            <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-8">
              <Input.Container className="desktop:w-1/2">
                <Input.Label htmlFor="name">{formDict.name}</Input.Label>
                <Input.Root
                  register={register}
                  name="name"
                  disabled={isLogged}
                  className={isLogged ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <Input.Error error={errors.name} />
              </Input.Container>
              <Input.Container className="desktop:w-1/2">
                <Input.Label htmlFor="surname">{formDict.surname}</Input.Label>
                <Input.Root
                  register={register}
                  name="surname"
                  disabled={isLogged}
                  className={isLogged ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <Input.Error error={errors.surname} />
              </Input.Container>
            </div>
            <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-8">
              <div className="w-full desktop:w-[calc(25%-1.875rem)]">
                <CountrySelect
                  countries={countries}
                  label={formDict.country}
                  id={'country-selector'}
                  onChange={({ code, name, id }) =>
                    handleSetCountry(code, name, id)
                  }
                  selectedValue={countries.find(
                    (option) => option.name === watch('country_info.name'),
                  )}
                />
              </div>
              <Input.Container className="xl:w-[calc(41.66667%-1.875rem)] desktop:w-[calc(33.33333%-1.875rem)]">
                <Input.Label htmlFor="email">{formDict.email}</Input.Label>
                <Input.Root
                  register={register}
                  name="email"
                  disabled={isLogged}
                  className={isLogged ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <Input.Error error={errors.email} />
              </Input.Container>
              <div className="flex flex-col flex-1">
                <label
                  className="text-sm text-gray-700 leading-[1.8]"
                  htmlFor="phone-country-selector">
                  {formDict.phone}
                </label>
                <div className="flex flex-row">
                  <div className="min-w-[140px] desktop:min-w-[120px] h-fit">
                    <CountrySelect
                      countries={countries}
                      label=""
                      id={'phone-country-selector'}
                      showPhoneCode
                      onChange={({ id, name }) => {
                        setValue('country_info.phone.code', id);
                        setValue('country_info.phone.country', name);
                      }}
                      selectedValue={countries.find(
                        (option) =>
                          option.name === watch('country_info.phone.country'),
                      )}
                      disabled={customer.phoneCode ? true : false}
                      buttonClassName={
                        customer.phoneCode
                          ? 'opacity-70 cursor-not-allowed'
                          : ''
                      }
                    />
                  </div>
                  <Input.Container className="w-full">
                    <Input.Root
                      id="phone-country-selector-content"
                      aria-label="Phone input field"
                      register={register}
                      name="phone"
                      disabled={customer.phone ? true : false}
                      className={
                        customer.phone ? 'opacity-70 cursor-not-allowed' : ''
                      }
                    />
                    <Input.Error error={errors.phone} />
                  </Input.Container>
                </div>
              </div>
            </div>

            <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-8">
              {isForExperience ? (
                <DatePicker.Root
                  open={modalIsOpen}
                  onOpenChange={setModalIsOpen}>
                  <div className="w-full desktop:w-1/2">
                    <DatePicker.Trigger
                      dict={datePickerDict}
                      confirmedDates={confirmedDates}
                      isOneDate
                    />
                    <Input.Error error={errors.dates?.start} />
                  </div>
                  <DatePicker.Content
                    confirmedDates={confirmedDates}
                    confirmDates={(values) => setValue('dates', values)}
                    onOpenChange={setModalIsOpen}
                    dict={datePickerDict}
                    isOneDate
                  />
                </DatePicker.Root>
              ) : (
                <DatePicker.Root
                  open={modalIsOpen}
                  onOpenChange={setModalIsOpen}>
                  <div className="w-full desktop:w-1/2">
                    <DatePicker.Trigger
                      dict={datePickerDict}
                      confirmedDates={confirmedDates}
                    />
                    {isForShortlist && showDatesError && (
                      <div className="flex flex-col mt-2">
                        <i className="text-error-primary font-averta-italic text-xs">
                          {formDict.errors.dates.checkIn}
                        </i>
                        <i className="text-error-primary font-averta-italic text-xs">
                          {formDict.errors.dates.checkOut}
                        </i>
                      </div>
                    )}
                  </div>
                  <DatePicker.Content
                    confirmedDates={confirmedDates}
                    confirmDates={(value) => {
                      setValue('dates', value);
                    }}
                    onOpenChange={setModalIsOpen}
                    dict={datePickerDict}
                  />
                </DatePicker.Root>
              )}

              <div
                className={`flex w-full ${
                  isModal ? 'tabletLg:w-[523px]' : 'tabletLg:w-[635px]'
                } desktop:w-1/2`}>
                <QuantitieInputRoot
                  className="w-full"
                  id="adults"
                  label={formDict.adults}>
                  <QuantitieInputBox
                    onChange={(value) => setValue('adults', value)}
                    value={watch('adults')}
                    max={1001}
                  />
                </QuantitieInputRoot>
                <QuantitieInputRoot
                  className="w-full"
                  id="childrens"
                  label={formDict.children}>
                  <QuantitieInputBox
                    onChange={(value) => setValue('children', value)}
                    value={watch('children')}
                    max={1002}
                  />
                </QuantitieInputRoot>
                <QuantitieInputRoot
                  className="w-full"
                  id="infants"
                  label={formDict.infants}>
                  <QuantitieInputBox
                    onChange={(value) => setValue('infants', value)}
                    value={watch('infants')}
                    max={1003}
                  />
                </QuantitieInputRoot>
              </div>
            </div>

            {isForShortlist && (
              <div className="flex flex-col">
                <div className="flex items-start gap-2 md:w-80 lg:w-[30rem] xl:w-full">
                  <input
                    className={cn('checkbox-input', {
                      'border border-underline': watch('flexible') == true,
                    })}
                    {...register('flexible')}
                    onChange={(e) => setValue('flexible', e.target.checked)}
                    type="checkbox"
                    name="flexible-checkbox"
                  />
                  <label
                    className="text-sm text-footer-gray mb-5"
                    htmlFor="flexible-checkbox">
                    {formDict.flexible}
                  </label>
                </div>
              </div>
            )}

            <div className="flex flex-col">
              <div className="flex items-start gap-2 md:w-80 lg:w-[30rem] xl:w-full">
                <input
                  className={cn('checkbox-input', {
                    'border border-underline': watch('terms') == true,
                  })}
                  {...register('terms')}
                  onChange={(e) => setValue('terms', e.target.checked)}
                  type="checkbox"
                  name="checkbox-input"
                  id="checkbox-input"
                />
                <label
                  className="text-sm text-footer-gray"
                  htmlFor="checkbox-input">
                  {formDict.terms}
                  <Link href={formDict.privacyHref} target="_blank">
                    <span className="hover:text-gray-900 underline cursor-pointer ml-1 text-footer-gray">
                      {formDict.privacyPolicy}
                    </span>
                  </Link>
                </label>
              </div>
              <Input.Error error={errors.terms} />
            </div>
            <TextArea
              className="mt-4"
              register={register}
              name="note"
              label={formDict.note}
              error={errors.note}
            />
            <div
              className={`flex justify-start relative tablet:justify-end ${
                isModal &&
                'flex-col-reverse tablet:justify-between w-full tablet:flex-row gap-y-2 '
              }`}>
              {isModal && (
                <Button
                  type="button"
                  onClick={() => onCancel && onCancel()}
                  variant={'outline'}
                  className={cn(
                    'tablet:w-auto ml-auto desktop:mb-0 tablet:ml-0',
                    {
                      'mb-16 tablet:mb-0': /iPhone|iPad|iPod/i.test(
                        navigator.userAgent,
                      ),
                    },
                  )}>
                  {formDict.cancel}
                </Button>
              )}

              <Button
                onClick={onSubmitClick}
                type="submit"
                className={cn('w-auto px-[30px]', {
                  'hover:bg-primary hover:text-white': blockSubmit,
                })}
                disabled={blockSubmit}>
                {formDict.sendEnquiry}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
